function throttle(fn, wait = 300) {
  let throttleTimeoutId = -1;
  let tick = false;

  return () => {
    clearTimeout(throttleTimeoutId);
    throttleTimeoutId = setTimeout(fn, wait);

    if (!tick) {
      fn.call();
      tick = true;
      setTimeout(() => {
        tick = false;
      }, wait);
    }
  };
}

if (!customElements.get('sticky-atc')) {
  customElements.whenDefined('product-form').then(() => {
    class StickyAtc extends customElements.get('product-form') {
      constructor() {
        super();

        this.section = this.closest('.section-main-product');
        this.mainProductForm = this.section.querySelector('.js-product');
        this.buyButtons = this.section.querySelector('buy-buttons');
        this.mainVariantPicker = this.section.querySelector('variant-picker');
        this.imageContainer = this.querySelector('.sticky-atc__bar .sticky-atc__image');

        // Popup elements
        this.popup = this.querySelector('.sticky-atc__popup');
        this.popupImage = this.querySelector('.sticky-atc__popup__image');
        this.addBtn = this.querySelector('.sticky-atc__popup__add');
        this.proxyButtons = this.querySelectorAll('.js-sticky-proxy');

        // Open / close
        this.querySelector('.js-open-sticky-popup')?.addEventListener('click', this.openPopup.bind(this));
        this.querySelectorAll('.js-close-sticky-popup').forEach((el) =>
          el.addEventListener('click', this.closePopup.bind(this))
        );
        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && !this.popup.hidden) this.closePopup();
        });

        // Proxy clicks
        this.proxyButtons.forEach((btn) =>
          btn.addEventListener('click', this.onProxyClick.bind(this))
        );

        // Scroll behaviour
        this.throttledOnScroll = throttle(StickyAtc.handleScroll.bind(this));
        window.addEventListener('scroll', this.throttledOnScroll);

        // Listen to the main picker
        this.section.addEventListener('on:variant:change', this.onVariantChange.bind(this));
        this.section.addEventListener('on:media-gallery:change', this.updateImage.bind(this));

        this.updateImage();
        StickyAtc.handleScroll.call(this);
        this.syncPopup();
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.throttledOnScroll);
      }

      /**
       * Opens the variant-selection popup.
       */
      openPopup() {
        this.syncPopup();
        this.popup.hidden = false;
        this.classList.add('sticky-atc--popup-active');
        document.body.classList.add('sticky-atc-popup-open');
      }

      /**
       * Closes the popup.
       */
      closePopup() {
        this.popup.hidden = true;
        this.classList.remove('sticky-atc--popup-active');
        document.body.classList.remove('sticky-atc-popup-open');
      }

      /**
       * Handles a click on a proxy option button -> reflects it onto the main picker.
       * @param {Event} evt
       */
      onProxyClick(evt) {
        const btn = evt.currentTarget;
        const optionName = btn.closest('[data-sticky-option]').dataset.stickyOption;
        const value = btn.dataset.stickyValue;
        if (!this.mainVariantPicker) return;

        const selector = this.mainVariantPicker.querySelector(
          `.option-selector[data-option="${CSS.escape(optionName)}"]`
        );
        if (!selector) return;

        if (selector.dataset.selectorType === 'dropdown') {
          const optionEl = selector.querySelector(
            `.custom-select__option[data-value="${CSS.escape(value)}"]`
          );
          if (optionEl) {
            const customSelect = optionEl.closest('custom-select');
            if (customSelect && customSelect.selectOption) {
              customSelect.selectOption(optionEl);
            }
          }
        } else {
          const input = Array.from(selector.querySelectorAll('input.js-option')).find(
            (i) => i.value === value
          );
          if (input) {
            input.checked = true;
            input.dispatchEvent(new CustomEvent('change', { bubbles: true, cancelable: false }));
          }
        }
        // syncPopup runs via the on:variant:change event the main picker fires.
      }

      /**
       * Mirrors the main picker state onto the popup (selected, availability, image, price, button).
       */
      syncPopup() {
        if (!this.mainVariantPicker) return;

        this.querySelectorAll('.sticky-atc__opt').forEach((optEl) => {
          const optionName = optEl.dataset.stickyOption;
          const selector = this.mainVariantPicker.querySelector(
            `.option-selector[data-option="${CSS.escape(optionName)}"]`
          );
          if (!selector) return;

          const isDropdown = selector.dataset.selectorType === 'dropdown';
          let selectedValue = null;

          if (isDropdown) {
            const sel = selector.querySelector('.custom-select__option[aria-selected="true"]');
            selectedValue = sel && sel.dataset.value !== '' ? sel.dataset.value : null;
          } else {
            const sel = selector.querySelector('input.js-option:checked');
            selectedValue = sel ? sel.value : null;
          }

          // Selected label text for color
          const selectedSpan = optEl.querySelector('.js-sticky-opt-selected');
          if (selectedSpan) selectedSpan.textContent = selectedValue || '';

          // Per-chip selected + availability state
          optEl.querySelectorAll('.js-sticky-proxy').forEach((chip) => {
            const val = chip.dataset.stickyValue;
            chip.classList.toggle('is-selected', val === selectedValue);

            let mainOpt;
            if (isDropdown) {
              mainOpt = selector.querySelector(
                `.custom-select__option[data-value="${CSS.escape(val)}"]`
              );
            } else {
              mainOpt = Array.from(selector.querySelectorAll('input.js-option')).find(
                (i) => i.value === val
              );
            }
            if (mainOpt) {
              chip.classList.toggle('is-unavailable', mainOpt.classList.contains('is-unavailable'));
              chip.classList.toggle('is-nonexistent', mainOpt.classList.contains('is-nonexistent'));
            }
          });
        });

        const variant = this.mainVariantPicker.variant;
        this.updatePopupImage(variant);
        this.updatePopupPrice(variant);
        this.updateAddButton(variant);
      }

      /**
       * Updates the popup add-to-cart button state.
       * @param {object} variant
       */
      updateAddButton(variant) {
        if (!this.addBtn) return;

        if (!variant) {
          this.addBtn.disabled = true;
          this.addBtn.textContent = this.addBtn.dataset.novariantText;
        } else if (!variant.available) {
          this.addBtn.disabled = true;
          this.addBtn.textContent = this.addBtn.dataset.unavailableText;
        } else {
          this.addBtn.disabled = false;
          this.addBtn.textContent = this.addBtn.dataset.addToCartText;
        }
      }

      /**
       * Updates the popup image based on the chosen variant.
       * @param {object} variant
       */
      updatePopupImage(variant) {
        if (!this.popupImage) return;
        const src = variant && variant.featured_image ? variant.featured_image.src : null;
        if (src) {
          const img = this.popupImage.querySelector('img');
          if (img) {
            img.src = src.replace(/(\.[a-z]+)(\?|$)/i, '_240x$1$2');
            img.srcset = '';
          }
        }
      }

      /**
       * Updates the popup price block.
       * @param {object} variant
       */
      updatePopupPrice(variant) {
        const priceEl = this.querySelector('.sticky-atc__popup__price');
        if (!priceEl || !variant) return;

        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          const discount = Math.round((variant.compare_at_price - variant.price) * 100 / variant.compare_at_price);
          priceEl.innerHTML = `
            <span class="sticky-atc__price--sale">${this.formatMoney(variant.price)}</span>
            <s class="sticky-atc__price--compare">${this.formatMoney(variant.compare_at_price)}</s>
            <span class="sticky-atc__price--badge">-${discount}%</span>
          `;
        } else {
          priceEl.innerHTML = `<span class="sticky-atc__price--regular">${this.formatMoney(variant.price)}</span>`;
        }
      }

      formatMoney(cents) {
        if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
          return Shopify.formatMoney(cents);
        }
        return '€\u00a0' + (cents / 100).toFixed(2).replace('.', ',');
      }

      /**
       * Handles submission of the product form (from the popup).
       * @param {object} evt - Event object.
       */
      async handleSubmit(evt) {
        evt.preventDefault();

        const variantId = this.section.querySelector('product-form buy-buttons input[name="id"]').value
          || this.form.querySelector('[name="id"]').value;
        if (!variantId) {
          // No variant chosen -> keep popup open, button stays disabled.
          return;
        }

        const customFormValid = this.mainProductForm.validate();
        if (!customFormValid || !this.mainProductForm.form.reportValidity()) {
          evt.preventDefault();
          if (!customFormValid) {
            this.closePopup();
            theme.scrollToRevealElement(this.mainProductForm);
          }
          return;
        }

        this.form.querySelectorAll('[data-copied]').forEach((el) => el.remove());
        const formData = new FormData(this.mainProductForm.form);
        for (const p of formData) {
          if (p[0] !== 'id' && !this.form.querySelector(`[name="${p[0]}"]`)) {
            const input = document.createElement('input');
            input.name = p[0];
            input.value = p[1];
            input.hidden = true;
            input.setAttribute('data-copied', '');
            this.form.append(input);
          }
        }
        super.handleSubmit(evt);
        this.closePopup();
      }

      /**
       * Determine sticky bar visibility after scroll.
       * Bar only shows when the ATC button has scrolled ABOVE the viewport (user has passed it).
       * It stays hidden when the button is in view OR still below the fold.
       */
      static handleScroll() {
        const topOffset = document.querySelector('.pageheader--sticky')
          ? document.querySelector('.section-header').offsetHeight
          : 0;
        const rect = this.buyButtons.getBoundingClientRect();

        // Only show bar when the button is fully above the viewport (scrolled past it)
        const showBar = rect.bottom < topOffset;
        this.classList.toggle('sticky-atc--out', !showBar);

        document.body.classList.toggle(
          'scrolled-to-bottom',
          window.scrollY + window.innerHeight + 100 > document.body.scrollHeight
        );
      }

      /**
       * Handle a change in variant on the page (fired by main picker).
       * @param {Event} evt
       */
      onVariantChange(evt) {
        this.updateImage();
        this.syncPopup();
        this.setErrorMsgState();
      }

      /**
       * Updates the sticky bar thumbnail image.
       */
      updateImage() {
        const mainImage = this.section.querySelector('.media-gallery .main-image .slider__item.is-active .product-media img');

        if (this.imageContainer) {
          if (mainImage) {
            this.imageContainer.innerHTML = mainImage.outerHTML;
          } else {
            this.imageContainer.textContent = '';
          }
        }
      }
    }

    customElements.define('sticky-atc', StickyAtc);
  });
}